/**
 * The portal's navigation.
 *
 * Built from `nav-config.ts` rather than written out, so what a role sees is
 * decided by one list of rules instead of a scattering of `isAdmin &&` in JSX.
 *
 * On a desktop it collapses to a strip of icons (with tooltips standing in for
 * the labels), because the leaderboard and the coach both want the width more
 * than they want the words. On a phone it is a panel that slides over the page
 * and closes itself once a link is chosen: leaving it open over the page just
 * navigated to would make every tap look like it did nothing.
 */
import { lazy, Suspense, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { ScandiSmile, ScandiWordmark } from "@/components/brand/scandi-wordmark";
import { NavUser } from "@/components/layout/nav-user";
import { PrefetchLink } from "@/components/prefetch-link";
import { isItemActive, MASTER_ITEM, SETTINGS_ITEM, visibleGroups, type NavBadge } from "@/components/layout/nav-config";
import { appNavItems } from "@/components/layout/apps-nav";
import { useSession } from "@/hooks/use-session";
import { usePendingUsersCount } from "@/hooks/use-users";
import { useTranslationActivity } from "@/features/translations/data";
import { isAdminRole } from "@scandi/shared";

// The ad jobs badge reads the studio's jobs list, whose contracts are not part of anyone's first paint.
const LiveJobsProbe = lazy(() => import("@/features/studio/jobs/list/live-count").then((m) => ({ default: m.LiveJobsProbe })));

export function AppSidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { role, isAdmin, isRealAdmin, studio, apps } = useSession();
  const appItems = appNavItems(apps);
  // The Master AI sits with the apps, for a real admin not viewing as someone else.
  const showMaster = isAdmin && isRealAdmin;
  const masterActive = isItemActive(MASTER_ITEM, pathname);
  const { setOpenMobile } = useSidebar();
  const pendingUsers = usePendingUsersCount();
  const translationActivity = useTranslationActivity({ enabled: isAdmin, slow: true }).data;
  const translationsLive = isAdmin && translationActivity ? translationActivity.running + translationActivity.queued : 0;
  // The reader's ad jobs in progress (all of them for an admin looking at Everyone); 0 hides the badge.
  const [agentJobsLive, setAgentJobsLive] = useState(0);
  const hasStudio = role !== null && (isAdminRole(role) || role === "strategist");

  const badges: Record<NavBadge, number> = { pendingUsers, translationsLive, agentJobsLive };
  const badgeLabel = (badge: NavBadge, count: number) =>
    badge === "translationsLive"
      ? t("translations.navBadge", { count })
      : badge === "agentJobsLive"
        ? t("studio.shell.nav.jobsBadge", { count })
        : t("nav.pendingBadge", { count });
  const closeOnPhone = () => setOpenMobile(false);
  const settingsActive = isItemActive(SETTINGS_ITEM, pathname);

  // The shell only renders for a session with a role; the fallback keeps the
  // types honest rather than handling a case that cannot reach here.
  const groups = role ? visibleGroups(role, { studio }) : [];

  return (
    <Sidebar
      collapsible="icon"
      mobileTitle={t("nav.sidebarTitle")}
      mobileDescription={t("nav.sidebarDescription")}
    >
      <SidebarHeader className="h-14 justify-center border-b border-sidebar-border/60 group-data-[collapsible=icon]:px-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              size="lg"
              tooltip={t("nav.portalName")}
              className="hover:bg-transparent active:bg-transparent group-data-[collapsible=icon]:size-8!"
            >
              <Link to="/" onClick={closeOnPhone} aria-label={t("nav.home")}>
                {/* The menu button sizes every svg inside it to an icon; the
                    wordmark is not an icon, hence the forced size. It is also
                    where the boot splash lands its logo. */}
                <span className="flex min-w-0 items-center gap-2.5 group-data-[collapsible=icon]:hidden">
                  <ScandiWordmark gradient data-brand-target="" className="h-7! w-auto! shrink-0" />
                  {/* A two-line lockup beside a hairline, so it reads as part of the
                      mark and the Spanish name fits the same width. Nudged down to
                      the lowercase letters' middle, which the d and i's ascenders
                      put below the middle of the wordmark's box. */}
                  <span className="border-sidebar-border text-muted-foreground max-w-[7.25rem] translate-y-[2px] border-l pl-2.5 text-[9px] leading-[1.35] font-medium tracking-[0.18em] uppercase">
                    {t("nav.portalName")}
                  </span>
                </span>
                {/* Collapsed to icons the word will not fit, so the smile stands in. */}
                <ScandiSmile className="hidden size-6! group-data-[collapsible=icon]:block" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="gap-0 pt-2">
        {hasStudio && (
          <Suspense fallback={null}>
            <LiveJobsProbe onCount={setAgentJobsLive} />
          </Suspense>
        )}
        {/* Separate apps given to this person, first so they are never below the
            fold: a full page load into another build, so a plain link. The
            Master AI follows them for real admins. Drawn only when there is one. */}
        {(appItems.length > 0 || showMaster) && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-[11px] tracking-wider uppercase">{t("nav.groupApps")}</SidebarGroupLabel>
            <SidebarMenu className="gap-0.5">
              {appItems.map((app) => (
                <SidebarMenuItem key={app.id}>
                  <SidebarMenuButton asChild tooltip={app.label}>
                    <a href={app.href}>
                      <app.icon />
                      <span>{app.label}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {showMaster && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={masterActive} tooltip={t(MASTER_ITEM.label)} className="data-active:[&>svg]:text-brand">
                    <PrefetchLink to={MASTER_ITEM.to} onClick={closeOnPhone} aria-current={masterActive ? "page" : undefined}>
                      <MASTER_ITEM.icon />
                      <span>{t(MASTER_ITEM.label)}</span>
                    </PrefetchLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroup>
        )}
        {groups.map((group) => (
          <SidebarGroup key={group.id}>
            <SidebarGroupLabel className="text-[11px] tracking-wider uppercase">
              {t(group.label)}
            </SidebarGroupLabel>
            <SidebarMenu className="gap-0.5">
              {group.items.map((item) => {
                const active = isItemActive(item, pathname);
                const count = item.badge ? badges[item.badge] : 0;
                const label = t(item.label);
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={label}
                      className="data-active:[&>svg]:text-brand relative"
                    >
                      <PrefetchLink
                        to={item.to}
                        onClick={closeOnPhone}
                        aria-current={active ? "page" : undefined}
                        // The count is drawn outside the link, so its meaning
                        // travels in the link's own name.
                        aria-label={count > 0 && item.badge ? `${label}, ${badgeLabel(item.badge, count)}` : undefined}
                      >
                        <item.icon />
                        <span>{label}</span>
                        {/* Collapsed to icons, the number is hidden, so a dot
                            keeps the "someone is waiting" signal visible. */}
                        {count > 0 && (
                          <span
                            aria-hidden
                            className="bg-brand-fill ring-sidebar absolute top-1 right-1 hidden size-2 rounded-full ring-2 group-data-[collapsible=icon]:block"
                          />
                        )}
                      </PrefetchLink>
                    </SidebarMenuButton>
                    {count > 0 && (
                      <SidebarMenuBadge className="bg-brand-fill text-brand-fill-foreground peer-data-active/menu-button:text-brand-fill-foreground peer-hover/menu-button:text-brand-fill-foreground rounded-full">
                        <span aria-hidden>{count}</span>
                      </SidebarMenuBadge>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}

        {/* Settings, for everyone: the one place every setting lives. At the foot of the list. */}
        <SidebarGroup className="mt-auto">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={settingsActive} tooltip={t(SETTINGS_ITEM.label)} className="data-active:[&>svg]:text-brand">
                <PrefetchLink to={SETTINGS_ITEM.to} onClick={closeOnPhone} aria-current={settingsActive ? "page" : undefined}>
                  <SETTINGS_ITEM.icon />
                  <span>{t(SETTINGS_ITEM.label)}</span>
                </PrefetchLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border/60">
        <NavUser />
      </SidebarFooter>
      <SidebarRail aria-label={t("nav.toggleSidebar")} title={t("nav.toggleSidebar")} />
    </Sidebar>
  );
}
